import React from "react";
import { Users, Lock } from "lucide-react";

const nameOf = (c) => c.name || `${c.first_name || ""} ${c.last_name || ""}`.trim() || c.email;

// One block per category. To = single contact (dropdown). CC is DERIVED automatically:
// everyone tagged to that division + anyone marked "always include" — shown as read-only chips.
export default function DivisionsEditor({ value = [], categoryOptions = [], contacts = [], onChange }) {
  const people = (contacts || []).filter((c) => c.email);
  const divFor = (cat) => value.find((d) => d.category === cat) || { category: cat, to_email: "" };
  const setTo = (cat, to_email) => {
    const exists = value.some((d) => d.category === cat);
    onChange(exists ? value.map((d) => (d.category === cat ? { ...d, to_email } : d)) : [...value, { category: cat, to_email }]);
  };

  if (!categoryOptions.length) {
    return <p className="rounded-md border border-dashed border-border px-3 py-5 text-center text-xs text-muted-foreground" data-testid="divisions-empty-cats">Add categories (above, or by tagging a contact's divisions) to set up divisions.</p>;
  }
  if (!people.length) {
    return <p className="rounded-md border border-dashed border-border px-3 py-5 text-center text-xs text-muted-foreground" data-testid="divisions-empty-contacts">Add contact persons (with email) and tag their divisions — they'll appear here automatically.</p>;
  }

  return (
    <div className="space-y-4" data-testid="divisions-editor">
      {categoryOptions.map((cat) => {
        const d = divFor(cat.value);
        const tagged = people.filter((c) => (c.divisions || []).includes(cat.value));
        const always = people.filter((c) => c.always_include && c.always_include_as !== "to");
        const toEmail = d.to_email || (tagged[0] && tagged[0].email) || "";
        const ccList = [];
        const seen = new Set(toEmail ? [toEmail.toLowerCase()] : []);
        [...tagged, ...always].forEach((c) => { const e = (c.email || "").toLowerCase(); if (e && !seen.has(e)) { ccList.push(c); seen.add(e); } });
        return (
          <div key={cat.value} className="rounded-lg border border-border p-4" data-testid={`division-${cat.value}`}>
            <div className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-slate-800">
              <Users className="h-4 w-4 text-[#1B4383]" />{cat.label}
            </div>
            <div>
              <label className="mb-1 block text-xs text-slate-500">To (primary recipient)</label>
              <select value={d.to_email || ""} onChange={(e) => setTo(cat.value, e.target.value)}
                className="h-9 w-full rounded-md border border-input bg-white px-2 text-sm" data-testid={`division-to-${cat.value}`}>
                <option value="">{tagged[0] ? `Auto — ${nameOf(tagged[0])}` : "— Select a contact —"}</option>
                {people.map((p) => <option key={p.email} value={p.email}>{nameOf(p)} ({p.email})</option>)}
              </select>
            </div>
            <div className="mt-2">
              <div className="mb-1 text-xs text-slate-500">CC (automatic)</div>
              {ccList.length ? (
                <div className="flex flex-wrap gap-1.5">
                  {ccList.map((c) => (
                    <span key={c.email} className="inline-flex items-center gap-1 rounded-full bg-[#E6ECF5] px-2 py-0.5 text-xs text-[#1B4383]" data-testid={`division-cc-${cat.value}-${c.email}`}>
                      {nameOf(c)}{c.always_include && <Lock className="h-3 w-3" title="Always included" />}
                    </span>
                  ))}
                </div>
              ) : <p className="text-xs text-muted-foreground">No CC yet — tag contacts to this division or mark someone "always include".</p>}
            </div>
          </div>
        );
      })}
    </div>
  );
}
